import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useT } from "@/context/I18nContext";
import { PLAY_STORE_URL, detectPlatform, isPromptDismissed, dismissPrompt } from "@/lib/device";
import BrandLogo from "@/components/common/BrandLogo";
import { X, Smartphone } from "lucide-react";

/** Google Play CTA — plain link, no auth, opens the official listing. */
export function PlayStoreButton({ variant = "dark", className = "", testId = "download-android" }) {
  const t = useT();
  const tone = variant === "light" ? "bg-white text-ink-900 hover:bg-cream-100" : "bg-ink-900 text-white hover:bg-ink-700";
  return (
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full font-medium transition-colors ${tone} ${className}`}
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" fill="currentColor"><path d="M3.6 2.3 13 11.7l-9.4 9.4c-.4-.3-.6-.8-.6-1.4V3.7c0-.6.2-1.1.6-1.4Zm1.6-.6c.3 0 .6.1.9.3l11 6.3-2.8 2.8L4.2 1.9c.3-.1.6-.2 1-.2Zm12.9 7.3 3.1 1.8c1 .6 1 1.9 0 2.5l-3.1 1.8-3.1-3.1 3.1-3Zm-1 7.1-11 6.3c-.3.2-.6.3-.9.3-.4 0-.7-.1-1-.2l10.1-10.2 2.8 2.8Z"/></svg>
      {t("download.android")}
    </a>
  );
}

export function IosComingSoon({ testId = "download-ios-soon" }) {
  const t = useT();
  return <span data-testid={testId} className="inline-flex items-center h-11 px-4 rounded-full border border-border bg-white/70 text-ink-500 text-sm">{t("download.iosSoon")}</span>;
}

/** Calm, dismissible, non-blocking app prompt for public pages. Remembered per browsing session only. */
export default function AppDownloadPrompt() {
  const t = useT();
  const [platform, setPlatform] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isPromptDismissed()) return;
    setPlatform(detectPlatform());
    const id = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(id);
  }, []);

  const close = () => { dismissPrompt(); setOpen(false); };
  if (!open || !platform) return null;

  return (
    <aside
      role="complementary"
      aria-label={t("download.title")}
      data-testid="app-download-prompt"
      data-platform={platform}
      className="fixed z-40 bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px] rounded-3xl bg-ink-900 text-cream-50 shadow-card-hover p-5 animate-fade-up"
    >
      <button type="button" onClick={close} aria-label={t("common.close") || "Close"} data-testid="app-prompt-close"
        className="absolute right-3 top-3 h-8 w-8 rounded-full inline-flex items-center justify-center text-cream-50/70 hover:text-white hover:bg-white/10">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-7">
        <div className="shrink-0 rounded-2xl bg-white/10 p-1.5"><BrandLogo size="md" /></div>
        <div className="min-w-0">
          <div className="font-heading text-lg leading-tight">{t("download.title")}</div>
          <p className="text-sm text-cream-50/80 mt-1.5 leading-relaxed">{t("download.body")}</p>
        </div>
      </div>
      {platform === "desktop" && (
        <div className="mt-4 flex items-center gap-4 rounded-2xl bg-white/5 p-3" data-testid="app-prompt-qr-wrap">
          <div className="rounded-xl bg-white p-1.5"><QRCodeSVG value={PLAY_STORE_URL} size={72} level="M" includeMargin={false} /></div>
          <div className="text-xs text-cream-50/80 leading-relaxed">{t("download.scan")}</div>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {platform === "ios" ? <IosComingSoon /> : <PlayStoreButton variant="light" />}
        <button type="button" onClick={close} data-testid="app-prompt-continue" className="h-11 px-4 rounded-full text-sm font-medium text-cream-50/85 hover:text-white hover:bg-white/10">
          {t("download.continue")}
        </button>
      </div>
      {platform === "desktop" && (
        <Link to="/download" onClick={close} data-testid="app-prompt-more" className="mt-3 inline-flex items-center gap-1.5 text-xs text-cream-50/70 hover:text-white">
          <Smartphone className="h-3.5 w-3.5" /> {t("download.more")}
        </Link>
      )}
    </aside>
  );
}
